
import Vue from 'vue'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { Component } from 'vue-property-decorator'
import { DistributorRelationship } from '../../global/models'
import RelationshipDisplay from '../../relationships/components/RelationshipDisplay.vue'
import { consumeError } from '../../global/utils'
import ConfirmModal from '../../global/components/ConfirmModal.vue'
import SplitSlider from '../components/SplitSlider.vue'
import DateRangeSelect from '../components/DateRangeSelect.vue'
import { downloadFile } from '../../global/functions/util'

@Component({
  components: { RelationshipDisplay, SplitSlider, DateRangeSelect }
})
export default class ProviderReadPage extends Vue {
  relationship: DistributorRelationship | null = null

  error: string | null = null
  dateFrom = null
  dateTo = null

  get relationshipId() {
    return this.$route.params.id
  }

  get disableConfirmModal() {
    return this.$refs.disableConfirmModal as ConfirmModal<DistributorRelationship>
  }

  get enableConfirmModal() {
    return this.$refs.enableConfirmModal as ConfirmModal<DistributorRelationship>
  }
  
  mounted() {
    this.getData()
  }

  isMigrationAgency () {
    return this.$store.getters['auth/isMigrationAgency']
  }

  async getData() {
    try {
      const response = await axios.get<DistributorRelationship>(`/distributors/${this.relationshipId}`)
      this.relationship = response.data
      if (this.relationship.distributorLimitPercentage) { this.relationship.distributorLimitPercentage = 100 - this.relationship.distributorLimitPercentage }
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 404:
            this.error = 'not-found'
            break
          case 403:
            this.error = 'unauthorised'
            break
          default:
            this.error = 'generic'
        }
      } else {
        this.error = 'generic'
      }
    }
  }

  editUser() {
    this.$router.push({ name: 'distributors-edit', params: { id: this.relationshipId.toString() } })
  }

  confirmDisableRelationship() {
    if (!this.relationship) {
      return
    }
    this.disableConfirmModal.open(this.relationship)
  }

  confirmEnableRelationship() {
    if (!this.relationship) {
      return
    }
    this.enableConfirmModal.open(this.relationship)
  }

  async disable(relationship: DistributorRelationship) {
    try {
      await axios.put(`/distributors/${relationship.id}/disable`)
      await this.getData()
    } catch (error) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  async enable(relationship: DistributorRelationship) {
    try {
      await axios.put(`/distributors/${relationship.id}/enable`)
      await this.getData()
    } catch (error) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  async deleteRelationship() {
    if (!this.relationship) {
      return
    }
    try {
      await axios.put(`/distributors/${this.relationship.id}/terminate`)
      this.$router.push({ name: 'distributors' })
    } catch (error) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  async generateReport() {
    if (!this.relationship) {
      return
    }
    downloadFile(`${axios.defaults.baseURL}/distributors/${this.relationship.id}/report?fromDate=${this.dateFrom}&toDate=${this.dateTo}`)
  }
}
